import { signal } from "@preact/signals";
import { useId } from "$store/sdk/useId.ts";
import Modal from "$store/components/ui/Modal.tsx";
import Icon from "$store/components/ui/Icon.tsx";
import { useEffect } from "preact/hooks";
import { useProductSelector } from "$store/sdk/useProductSelector.tsx";
import { Picture, Source } from "apps/website/components/Picture.tsx";
import type { ImageWidget } from "apps/admin/widgets.ts";

export interface Props {
  image: {
    src: ImageWidget;
    alt?: string;
  };
  label?: string;
  link?: string;
  variant?: "media-banner";
}

const buttonStyleVariant = {
  "media-banner": "h-[443px] h-[200px] w-full p-4 lg:p-0",
};

const bannerStyleVariant = {
  "media-banner": "!opacity-100",
};

const isOpen = signal(false);

export default function Media({ image, label, link, variant }: Props) {
  const modalId = useId();

  useEffect(() => {
    const { selectedProducts } = useProductSelector();
    if (selectedProducts.peek().length) selectedProducts.value = [];
  }, [image]);

  return (
    <>
      <label
        class={`cursor-pointer group ${
          variant ? buttonStyleVariant[variant] : ""
        }`}
        htmlFor={modalId}
        aria-label="Abrir o modal do vídeo"
        onClick={() => {
          isOpen.value = true;
        }}
      >
        <Picture>
          <Source
            media="(max-width: 767px)"
            src={image.src}
            width={341}
            height={288}
          />

          <Source
            media="(min-width: 768px)"
            src={image.src}
            width={variant ? 1000 : 553}
            height={466}
          />

          <img
            class={`w-full h-full max-h-[29rem] rounded-lg object-cover ${
              variant ? bannerStyleVariant[variant] : ""
            }`}
            src={image.src}
            alt={image.alt}
            decoding="async"
            loading="lazy"
            width={341}
            height={288}
          />
        </Picture>

        {link && (
          <Icon
            id="Play"
            class="opacity-80 text-white absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 transition-opacity duration-300 lg:group-hover:opacity-100"
            size={82}
          />
        )}

        {label && (
          <span class="max-w-[75%] py-1 pl-8 pr-6 rounded bg-tw-primary-100 text-white text-2xl font-maven font-extrabold flex items-center absolute top-4 -right-2 z-10 lg:pr-3 lg:pl-28 lg:py-2 lg:-right-6 lg:text-3xl">
            {label}
          </span>
        )}
      </label>

      {link && isOpen.value && (
        <Modal
          id={modalId}
          class="max-w-max p-0 rounded-none shadow-none bg-transparent"
        >
          <div class="pt-11 pb-10 relative lg:mx-10">
            <iframe
              class="w-screen max-w-full px-2 aspect-video md:max-w-3xl"
              src={link}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              loading="lazy"
            />

            <label
              class="w-10 h-10 rounded-full bg-white flex justify-center items-center absolute top-0 right-0 cursor-pointer lg:-right-10"
              title="Fechar o modal do vídeo"
              aria-label="Fechar o modal do vídeo"
              htmlFor={modalId}
            >
              <Icon id="Close" size={22} />
            </label>
          </div>
        </Modal>
      )}
    </>
  );
}
